import { registerDestructor } from '@ember/destroyable';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { t } from 'ember-intl';
import Modifier from 'ember-modifier';
import Hls from 'hls.js';
import type { ErrorData } from 'hls.js';

import imageUrl from 'umg-ember-backend/helpers/image-url';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

interface LoadHlsStreamSignature {
  Args: {
    Positional: [asset: TODO];
    Named: {
      onError: (error: ErrorData) => void;
    };
  };
  Element: HTMLMediaElement;
}

const setupHlsStream = class LoadHlsStreamModifier extends Modifier<LoadHlsStreamSignature> {
  hls?: Hls;

  // Hooks
  modify(
    element: LoadHlsStreamSignature['Element'],
    [asset]: LoadHlsStreamSignature['Args']['Positional'],
    { onError }: LoadHlsStreamSignature['Args']['Named'],
  ) {
    if (!asset) {
      return;
    }

    if (Hls && Hls.isSupported()) {
      const hls = new Hls();
      hls.attachMedia(element);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(asset.signedVideoUrl);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        onError(data);
      });

      this.hls = hls;
    }

    registerDestructor(this, () => this.destroyStream.bind(this));
  }

  destroyStream() {
    this.hls?.destroy();
  }
};

interface VideoStreamSignature {
  Args: {
    asset: TODO;
  };
  Element: HTMLMediaElement;
}

export default class UiVideoStreamComponent extends Component<VideoStreamSignature> {
  // Defaults
  setupHlsStream = setupHlsStream;
  @tracked error?: ErrorData;

  // Actions
  onStreamError = (error: ErrorData) => {
    this.error = error;
  };

  // Template
  <template>
    <div class={{(bem)}}>
      {{#if @asset.isAudio}}
        <audio
          controls={{true}}
          {{this.setupHlsStream @asset onError=this.onStreamError}}
          ...attributes
        ></audio>
      {{else}}
        <video
          controls={{true}}
          poster={{imageUrl @asset '4/1280'}}
          {{this.setupHlsStream @asset onError=this.onStreamError}}
          ...attributes
        ></video>
      {{/if}}

      {{#if this.error.fatal}}
        <div class={{bem 'convert-message'}}>
          {{t 'ui.video_stream.convert_message'}}<br />
        </div>
      {{/if}}
    </div>
  </template>
}
